/**
 * ORDEM DAS SEÇÕES DA HOME — contrato de render.
 *
 * ⚠️ ESPELHO EXATO de `/root/imobtotal-app/src/site-builder/site-section-order.config.ts`
 * (mesmo padrão do `theme-preview.ts`). Mudou aqui, mude LÁ — e vice-versa.
 * Os dois lados têm spec de unidade cobrindo `normalizeSectionOrder`.
 *
 * Por que existe: até 08/2026 a ordem dos blocos da home era HARDCODED no JSX de
 * `pages/index.tsx` — a ponto de existirem condicionais por tenant (`site.id == 2551`,
 * `2226`, `2291`, `2052/2523`) só pra reposicionar bloco. O Builder passa a gravar
 * uma ordem por tenant; aqui fica o contrato que o render consome.
 *
 * REGRA DE OURO: `normalizeSectionOrder` NUNCA pode omitir um slot. Um array salvo
 * antes de um slot novo existir faria a seção sumir do ar nos ~300 sites.
 */

/** Slots FIXOS: não entram no rearranjo (overlays e hacks posicionais por tenant). */
export const FIXED_HOME_SLOTS = [
  '_popup',
  '_destaques-2551',
  '_banner-2226',
  '_banner-2291',
  '_simulador',
  '_banner-2052',
] as const;

/**
 * Ordem default do miolo da home — byte-a-byte a sequência do JSX antes do refactor.
 * `topo`, `busca` (hero) e `rodape` NÃO entram: são fixos fora do array.
 */
export const DEFAULT_HOME_ORDER: string[] = [
  'selection',
  'banner',
  'cidades',
  '_popup',
  'destaques',
  'card',
  'empreendimentos',
  'redes',
  'sobre',
  'equipe',
  'construtoras',
  'depoimento',
  '_destaques-2551',
  '_banner-2226',
  'instagram',
  '_banner-2291',
  'fundo',
  '_simulador',
  '_banner-2052',
  'blogs',
];

const FIXED = new Set<string>(FIXED_HOME_SLOTS);

/** Slots que o usuário pode rearranjar (tudo que não é fixo). */
export const ORDERABLE_HOME_SLOTS: string[] = DEFAULT_HOME_ORDER.filter((k) => !FIXED.has(k));

/**
 * Normaliza uma ordem vinda do banco/querystring:
 * - descarta chave desconhecida e duplicata;
 * - respeita a ordem pedida SÓ para os slots ordenáveis;
 * - reinsere os ausentes e os FIXOS na posição default.
 *
 * Garantia: o retorno contém exatamente os slots de `defaults`, uma vez cada.
 *
 * Nota sobre array PARCIAL: o caso real é "ordem salva antes de um slot novo existir"
 * — todos os antigos presentes, um ausente, que volta na vizinhança default. Uma lista
 * com poucos slots é completada em direção à default, então a posição dos poucos
 * informados não é garantida. A UI do Builder sempre grava a lista COMPLETA.
 */
export function normalizeSectionOrder(
  input: unknown,
  defaults: string[] = DEFAULT_HOME_ORDER,
): string[] {
  const known = new Set(defaults);
  const fixedAt = new Map<number, string>();
  defaults.forEach((key, i) => {
    if (FIXED.has(key)) fixedAt.set(i, key);
  });

  const wanted: string[] = [];
  const seen = new Set<string>();
  if (Array.isArray(input)) {
    for (const raw of input) {
      if (typeof raw !== 'string') continue;
      const key = raw.trim();
      if (!known.has(key) || FIXED.has(key) || seen.has(key)) continue;
      seen.add(key);
      wanted.push(key);
    }
  }

  // Ordenáveis ausentes voltam na posição default (relativa aos demais ordenáveis).
  const orderable = defaults.filter((k) => !FIXED.has(k));
  if (wanted.length !== orderable.length) {
    const missing = orderable.filter((k) => !seen.has(k));
    for (const key of missing) {
      const at = orderable.indexOf(key);
      // insere antes do primeiro já-posicionado que vinha DEPOIS dele no default
      const idx = wanted.findIndex((k) => orderable.indexOf(k) > at);
      if (idx < 0) wanted.push(key);
      else wanted.splice(idx, 0, key);
    }
  }

  // Recompõe intercalando os fixos nos índices absolutos do default.
  const out: string[] = [];
  let cursor = 0;
  for (let i = 0; i < defaults.length; i++) {
    const fixed = fixedAt.get(i);
    if (fixed) out.push(fixed);
    else out.push(wanted[cursor++]);
  }
  return out;
}

/** `true` quando a ordem é igual à default (usado pra não gravar/serializar à toa). */
export function isDefaultOrder(order: string[] | null | undefined, defaults: string[] = DEFAULT_HOME_ORDER): boolean {
  if (!order) return true;
  const normalized = normalizeSectionOrder(order, defaults);
  return normalized.every((k, i) => k === defaults[i]);
}
